import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Star, BadgeCheck, Send } from "lucide-react";
import { toast } from "sonner";

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={15} className={i <= n ? "text-yellow-400 fill-yellow-400" : "text-gray-600"} />
      ))}
    </div>
  );
}

export function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", comment: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Yorumunuz onaya gönderildi. Teşekkürler!");
      setForm({ name: "", username: "", comment: "", rating: 5 });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const row = [...reviews, ...reviews];
  return (
    <section className="py-16 sm:py-20 overflow-hidden" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="text-xs font-medium text-[#d8b4fe] uppercase tracking-[0.2em]">Müşteri Yorumları</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3">Binlerce Mutlu Müşteri</h2>
        <p className="text-gray-400 mt-3 max-w-xl mx-auto">Gerçek müşterilerimizin deneyimleri.</p>
      </div>
      {reviews.length > 0 && (
        <div className="marquee-wrap overflow-hidden mt-10 relative">
          <div className="absolute left-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-r from-[#1f222d] to-transparent" />
          <div className="absolute right-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-l from-[#1f222d] to-transparent" />
          <div className="marquee-track gap-4 px-4">
            {row.map((r, i) => (
              <div key={i} className="card-surface p-5 w-[300px] shrink-0 text-left" data-testid="review-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#d8b4fe] to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                      {r.name?.[0] || "?"}
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-white text-sm font-semibold">{r.name}<BadgeCheck size={13} className="text-[#3897f0]" /></div>
                      <div className="text-xs text-gray-500">{r.username}</div>
                    </div>
                  </div>
                  <Stars n={r.rating} />
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="text-center mt-10">
        <button onClick={() => setOpen((o) => !o)} data-testid="toggle-review-form-btn"
          className="inline-flex items-center gap-2 card-surface card-hover px-6 py-3 font-semibold text-white">
          <Send size={16} /> Yorum Yap
        </button>
      </div>
      {open && (
        <form onSubmit={submit} className="card-surface max-w-lg mx-auto mt-6 p-6 space-y-3 text-left" data-testid="review-form">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Adınız" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-[#171922] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#d8b4fe] outline-none" data-testid="review-name-input" />
            <input placeholder="@kullaniciadi" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="bg-[#171922] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#d8b4fe] outline-none" data-testid="review-username-input" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">Puan:</span>
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={22} onClick={() => setForm({ ...form, rating: i })}
                className={`cursor-pointer ${i <= form.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-600"}`} data-testid={`review-star-${i}`} />
            ))}
          </div>
          <textarea required placeholder="Yorumunuz..." rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
            className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#d8b4fe] outline-none" data-testid="review-comment-input" />
          <button type="submit" disabled={submitting} className="btn-gradient w-full py-3 rounded-xl font-bold" data-testid="submit-review-btn">
            {submitting ? "Gönderiliyor..." : "Gönder"}
          </button>
        </form>
      )}
    </section>
  );
}
